import { useId, useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BitrixUserLink, User } from '@chatapp-inbox/shared';
import { setBitrixUserLink } from '../../api';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { adminErrorMessage } from './error-message';
import { bitrixUserLinksKey } from './keys';
import { useBitrixConnection, useBitrixOptions, useBitrixUserLinks } from './use-bitrix-admin';
import styles from './forms.module.css';
import panelStyles from './EditUserPanel.module.css';

const AUTOMATIC = '';

interface UserBitrixBlockProps {
  user: User;
}

/**
 * "Usuário no Bitrix": qual usuário do Bitrix é esta pessoa. É para ele que o CladvApp passa a conversa quando a
 * pessoa assume, e é em nome dele que as respostas entram no chat do Bitrix. Em "Automático" vale o usuário do Bitrix
 * com o mesmo e-mail. Só aparece com o Bitrix conectado.
 */
export function UserBitrixBlock({ user }: UserBitrixBlockProps): ReactElement | null {
  const errorHeadingId = useId();
  const connection = useBitrixConnection();
  const connected = connection.data?.status === 'connected';
  const options = useBitrixOptions(connected);
  const links = useBitrixUserLinks(connected);
  if (!connected) return null;
  if (options.isPending || links.isPending) return null;
  if (options.isError || links.isError) {
    return (
      <section aria-labelledby={errorHeadingId} className={panelStyles.block}>
        <h4 id={errorHeadingId} className={panelStyles.blockTitle}>
          Usuário no Bitrix
        </h4>
        <p role="alert" className={styles.alert}>
          Não foi possível carregar os usuários do Bitrix agora.
        </p>
      </section>
    );
  }
  const current = links.data.find((l) => l.userId === user.id)?.bitrixUserId ?? AUTOMATIC;
  return (
    <UserBitrixForm key={user.id} user={user} current={current} bitrixUsers={options.data.users} />
  );
}

interface FormProps {
  user: User;
  current: string;
  bitrixUsers: Array<{ id: string; name: string; email: string | null }>;
}

function UserBitrixForm({ user, current, bitrixUsers }: FormProps): ReactElement {
  const headingId = useId();
  const selectId = useId();
  const hintId = useId();
  const queryClient = useQueryClient();
  const status = useStatusMessage();
  const [value, setValue] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (bitrixUserId: string | null) => setBitrixUserLink(user.id, bitrixUserId),
    onSuccess: (link) => {
      queryClient.setQueryData<BitrixUserLink[]>(bitrixUserLinksKey, (old = []) => [
        ...old.filter((l) => l.userId !== user.id),
        ...(link ? [link] : []),
      ]);
      status.announce(
        link ? 'Vínculo com o usuário do Bitrix salvo.' : 'Vínculo desfeito: vale o mesmo e-mail.',
      );
    },
    onError: (failure) =>
      setError(adminErrorMessage(failure, 'Não foi possível salvar o vínculo. Tente de novo.')),
  });

  // O usuário vinculado pode ter saído do Bitrix: a escolha atual continua aparecendo.
  const known = bitrixUsers.some((u) => u.id === current);
  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (save.isPending || value === current) return;
    setError(null);
    save.mutate(value === AUTOMATIC ? null : value);
  };

  return (
    <section aria-labelledby={headingId} className={panelStyles.block}>
      <h4 id={headingId} className={panelStyles.blockTitle}>
        Usuário no Bitrix
      </h4>
      <form onSubmit={submit} className={styles.form}>
        <label htmlFor={selectId} className={styles.label}>
          Quem é esta pessoa no Bitrix
        </label>
        <select
          id={selectId}
          className={styles.input}
          value={value}
          aria-describedby={hintId}
          onChange={(e) => setValue(e.target.value)}
          disabled={save.isPending}
        >
          <option value={AUTOMATIC}>Automático (o usuário do Bitrix de mesmo e-mail)</option>
          {!known && current !== AUTOMATIC && (
            <option value={current}>Usuário {current} do Bitrix</option>
          )}
          {bitrixUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.email ? `${u.name} (${u.email})` : u.name}
            </option>
          ))}
        </select>
        <p id={hintId} className={styles.hint}>
          É para este usuário que o CladvApp passa a conversa no Bitrix quando {user.name} a assume,
          e é em nome dele que as respostas entram no chat do Bitrix.
        </p>
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            busy={save.isPending}
            disabled={value === current}
          >
            Salvar vínculo
          </Button>
        </div>
      </form>
      {error && (
        <p role="alert" className={styles.alert}>
          {error}
        </p>
      )}
      <StatusRegion message={status.message} />
    </section>
  );
}
